import React, { useState, useEffect } from 'react';
import { Customer } from '../../types';
import { createCustomer, updateCustomer, CustomerCreate, CustomerUpdate } from '../../services/customerService';
import Modal from '../shared/Modal';

interface CustomerFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  customer?: Customer | null; // null/undefined = create mode
  storeId: number;
}

const CustomerFormModal: React.FC<CustomerFormModalProps> = ({
  isOpen,
  onClose,
  onSaved,
  customer,
  storeId,
}) => {
  const isEdit = !!customer;

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [mainPhone, setMainPhone] = useState('');
  const [secondPhone, setSecondPhone] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [country, setCountry] = useState('United States');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (customer) {
        setFirstName(customer.firstName);
        setLastName(customer.lastName);
        setEmail(customer.email);
        setMainPhone(customer.mainPhone || '');
        setSecondPhone(customer.secondPhone || '');
        setAddress(customer.address || '');
        setCity(customer.city || '');
        setState(customer.state || '');
        setZipCode(customer.zipCode || '');
        setCountry(customer.country || 'United States');
      } else {
        setFirstName('');
        setLastName('');
        setEmail('');
        setMainPhone('');
        setSecondPhone('');
        setAddress('');
        setCity('');
        setState('');
        setZipCode('');
        setCountry('United States');
      }
      setError(null);
    }
  }, [isOpen, customer]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      setError('First name, last name, and email are required.');
      return;
    }

    setSaving(true);
    try {
      if (isEdit && customer) {
        const data: CustomerUpdate = {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim(),
          main_phone: mainPhone.trim() || undefined,
          second_phone: secondPhone.trim() || undefined,
          address: address.trim() || undefined,
          city: city.trim() || undefined,
          state: state.trim() || undefined,
          zip_code: zipCode.trim() || undefined,
          country: country.trim() || undefined,
        };
        await updateCustomer(customer.id, data);
      } else {
        const data: CustomerCreate = {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim(),
          main_phone: mainPhone.trim() || undefined,
          second_phone: secondPhone.trim() || undefined,
          address: address.trim() || undefined,
          city: city.trim() || undefined,
          state: state.trim() || undefined,
          zip_code: zipCode.trim() || undefined,
          country: country.trim() || undefined,
        };
        await createCustomer(data, storeId);
      }
      onSaved();
      onClose();
    } catch (err: any) {
      console.error('Error saving customer:', err);
      setError(err.message || 'Failed to save customer');
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <>
      <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
        Cancel
      </button>
      <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
        {saving ? 'Saving...' : isEdit ? 'Update Customer' : 'Create Customer'}
      </button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Customer' : 'Add Customer'}
      size="medium"
      footer={footer}
    >
      <form onSubmit={handleSubmit} className="form-modal-body">
        {error && <div className="form-error-banner">{error}</div>}

        <div className="form-row">
          <div className="form-group">
            <label>First Name *</label>
            <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Last Name *</label>
            <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} required />
          </div>
        </div>

        <div className="form-group">
          <label>Email *</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} required />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Phone</label>
            <input type="text" value={mainPhone} onChange={e => setMainPhone(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Secondary Phone</label>
            <input type="text" value={secondPhone} onChange={e => setSecondPhone(e.target.value)} />
          </div>
        </div>

        <div className="form-group">
          <label>Address</label>
          <input type="text" value={address} onChange={e => setAddress(e.target.value)} />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>City</label>
            <input type="text" value={city} onChange={e => setCity(e.target.value)} />
          </div>
          <div className="form-group">
            <label>State</label>
            <input type="text" value={state} onChange={e => setState(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Zip Code</label>
            <input type="text" value={zipCode} onChange={e => setZipCode(e.target.value)} />
          </div>
        </div>

        <div className="form-group">
          <label>Country</label>
          <input type="text" value={country} onChange={e => setCountry(e.target.value)} />
        </div>
      </form>
    </Modal>
  );
};

export default CustomerFormModal;
